import React, { useCallback, useMemo, useState } from 'react';
import styled from 'astroturf/react';

import { format, type FormatResult } from 'js/citation_generator/format';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Dropdown } from 'js/design_system/forms/Dropdown';
import { Tabs } from 'js/design_system/Tabs';

import {
  BibliographyEntry,
  nextBibliographyId,
  sortBibliography,
} from './bibliography';
import { BibliographyList } from './BibliographyList';
import { CitationOutput } from './CitationOutput';
import {
  emptyForm,
  FormState,
  hasCitableContent,
  toCslJson,
} from './formState';
import { PapersSearch } from './PapersSearch';
import { resultToFormState, WorkSearchResult } from './resultToFormState';
import { SourceForm } from './SourceForm';
import { SOURCE_TYPES, SourceType } from './sourceTypes';
import {
  CITATION_STYLES,
  CitationStyle,
  CitationStyleId,
  DEFAULT_HUB_STYLE,
} from './styles';

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
  padding: var(--spacing-xs-16, 16px);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--background-white, #ffffff);
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-16, 16px);
  align-items: center;
  padding-top: var(--spacing-xs-16, 16px);
`;

const ManualWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
  padding-top: var(--spacing-xs-16, 16px);
`;

type Mode = 'search' | 'manual';

type CitationGeneratorProps = {
  defaultStyle?: CitationStyleId;
  styles?: CitationStyle[];
};

function tryFormat(
  item: Record<string, unknown>,
  styleId: CitationStyleId,
): FormatResult | null {
  if (!hasCitableContent(item)) return null;
  try {
    return format(item, styleId);
  } catch {
    return null;
  }
}

type SearchPanelProps = {
  onSelect: (result: WorkSearchResult) => void;
  onEnterManually: (query: string) => void;
};

function SearchPanel({
  onSelect,
  onEnterManually,
}: SearchPanelProps): React.ReactElement {
  return <PapersSearch onSelect={onSelect} onEnterManually={onEnterManually} />;
}

type ManualPanelProps = {
  form: FormState;
  sourceTabs: Array<{
    key: string;
    renderTitle: () => string;
    render: () => null;
  }>;
  onSourceType: (key: string) => void;
  onChangeForm: (form: FormState) => void;
  onAddToBibliography: () => void;
};

function ManualPanel({
  form,
  sourceTabs,
  onSourceType,
  onChangeForm,
  onAddToBibliography,
}: ManualPanelProps): React.ReactElement {
  return (
    <ManualWrap>
      <Body size="sm">* Required fields</Body>
      <Tabs
        tab={form.type}
        overflowMode="wrap"
        renderMode="active"
        onTabChange={onSourceType}
        tabs={sourceTabs}
      />
      <SourceForm form={form} onChange={onChangeForm} />
      <FormFooter>
        <Button type="button" size="sm" onClick={onAddToBibliography}>
          Add to bibliography
        </Button>
        <Body size="sm">
          Live citation updates as you type. Bibliography stays in this session
          only.
        </Body>
      </FormFooter>
    </ManualWrap>
  );
}

export function CitationGenerator({
  defaultStyle = DEFAULT_HUB_STYLE,
  styles = CITATION_STYLES,
}: CitationGeneratorProps): React.ReactElement {
  const [styleId, setStyleId] = useState<CitationStyleId>(defaultStyle);
  const [mode, setMode] = useState<Mode>('search');
  const [form, setForm] = useState<FormState>(() => emptyForm());
  const [bibliography, setBibliography] = useState<BibliographyEntry[]>([]);

  const selectedStyle =
    styles.find((style) => style.id === styleId) ?? styles[0];

  const liveItem = useMemo(() => toCslJson(form), [form]);
  const liveResult = useMemo(
    () => tryFormat(liveItem, styleId),
    [liveItem, styleId],
  );

  const bibliographyHtml = useMemo(() => {
    return sortBibliography(bibliography, styleId)
      .map((entry) => tryFormat(entry.cslJson, styleId)?.referenceEntry)
      .filter((html): html is string => Boolean(html));
  }, [bibliography, styleId]);

  const onSourceType = useCallback((key: string): void => {
    const nextType = key as SourceType;
    setForm((current) => ({ ...current, type: nextType }));
  }, []);

  const onAddToBibliography = useCallback((): void => {
    const item = toCslJson(form);
    if (!hasCitableContent(item)) return;
    setBibliography((current) =>
      current.concat({ id: nextBibliographyId(), cslJson: item }),
    );
  }, [form]);

  const onSelectPaper = useCallback((result: WorkSearchResult): void => {
    setForm(resultToFormState(result));
    setMode('manual');
  }, []);

  const onEnterManually = useCallback((query: string): void => {
    setForm((current) => ({ ...current, title: query }));
    setMode('manual');
  }, []);

  const sourceTabs = useMemo(
    () =>
      SOURCE_TYPES.map((source) => ({
        key: source.id,
        renderTitle: () => source.label,
        render: () => null,
      })),
    [],
  );

  const modeTabs = useMemo(
    () => [
      {
        key: 'search',
        renderTitle: () => 'Search papers',
        render: function renderSearch() {
          return (
            <SearchPanel
              onSelect={onSelectPaper}
              onEnterManually={onEnterManually}
            />
          );
        },
      },
      {
        key: 'manual',
        renderTitle: () => 'Enter manually',
        render: function renderManual() {
          return (
            <ManualPanel
              form={form}
              sourceTabs={sourceTabs}
              onSourceType={onSourceType}
              onChangeForm={setForm}
              onAddToBibliography={onAddToBibliography}
            />
          );
        },
      },
    ],
    [
      form,
      sourceTabs,
      onSourceType,
      onSelectPaper,
      onEnterManually,
      onAddToBibliography,
    ],
  );

  return (
    <Shell>
      <Toolbar>
        <Dropdown
          label="Citation style"
          value={selectedStyle}
          options={styles}
          keyExtractor={(style: CitationStyle) => style.id}
          renderOption={(style: CitationStyle) => style.name}
          renderValue={(style: CitationStyle) => style.name}
          onChange={(style: CitationStyle) => setStyleId(style.id)}
        />
      </Toolbar>
      <Tabs
        tab={mode}
        overflowMode="wrap"
        renderMode="active"
        onTabChange={(key) => setMode(key as Mode)}
        tabs={modeTabs}
      />
      <CitationOutput result={liveResult} />
      <BibliographyList styleId={styleId} entries={bibliographyHtml} />
    </Shell>
  );
}
